import { toast } from "react-toastify";
import { NOACCESS } from "../constants/routes";
import * as routes from "../constants/routes";
import { ROLES } from "../constants/roleConstants";
import { getCookie } from "../store/localStorage";

export function buildSortParams(config) {
  // enable it when multiple sorting
  // const sortingConfigStr = Object.entries(config).reduce((acc, [key, value]) => {
  //   if (value !== null) {
  //     acc += `${key} ${value},`;
  //   }
  //   return acc;
  // }, '').replace(/,$/, ''); // Remove trailing comma

  // console.log('sortingConfigStr', sortingConfigStr);

  const entries = Object.entries(config).filter(([_, value]) => value !== null);

  const [[, value]] = Object.entries(config) || []; // For multiple sort columns, direction is single now

  return {
    col: entries.map(([key]) => key).join(","),
    // val: entries.map(([_, value]) => value).join(',') // use it when multiple sorting
    val: value,
  };
}

export const onErrorToast = (msg) => {
  const userData = getCookie("userData");
  const message = replaceHierarchyTermsWithAlias(msg, userData);

  toast(message, {
    type: "error",
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: false,
    progress: undefined,
    theme: "colored",
  });
};

const HIERARCHY_MENU_NAMES = ["REGION", "PRACTICE", "SUB PRACTICE"];

export const actionColumnWidth = "3%";
export const deleteColumnWidth = "4%";
export const disableColumnWidth = "8%";
export const dateColumnWidth = "10%";

const HIERARCHY_ROUTE_MAP = [
  { route: routes.REGION, menuName: "Region" },
  { route: routes.PRACTICE, menuName: "Practice" },
  { route: routes.SUBPRACTICE, menuName: "Sub Practice" },
];

const FIXED_HIERARCHY_LABELS = [
  "Practice Manager",
  "Practice Director",
  "Encore Practice Director",
  "Local Practice Director",
];

const normalizeMenuName = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[-_\s]+/g, "");

const normalizeRoleCode = (value) => normalizeMenuName(value);

const parseReplaceLabels = (replaceLabels) => {
  if (Array.isArray(replaceLabels)) {
    return replaceLabels
      .flatMap((label) => String(label || "").split(","))
      .map((label) => label.trim().replace(/^['\"]|['\"]$/g, ""))
      .filter(Boolean);
  }

  if (typeof replaceLabels === "string") {
    return replaceLabels
      .split(",")
      .map((label) => label.trim().replace(/^['\"]|['\"]$/g, ""))
      .filter(Boolean);
  }

  return [];
};

export const isRoleAllowed = (allowedRoles, roleCode) => {
  const normalizedRoleCode = normalizeRoleCode(roleCode);
  const normalizedAllowedRoles = Array.isArray(allowedRoles)
    ? allowedRoles.map((role) => normalizeRoleCode(role))
    : [];

  return normalizedAllowedRoles.includes(normalizedRoleCode);
};

const getAccessibleHierarchyMenuSet = (userData) => {
  const menuList = Array.isArray(userData?.AccessibleHierarchyMenus)
    ? userData.AccessibleHierarchyMenus
    : [];

  return new Set(menuList.map((menuName) => normalizeMenuName(menuName)));
};

const getHierarchyReplacementEntries = (userData) => {
  const aliasItems = Array.isArray(userData?.HierarchyMenuAliases)
    ? userData.HierarchyMenuAliases
    : [];

  const replacementMap = new Map();

  aliasItems.forEach((item) => {
    const menuName = String(item?.MenuName || "").trim();
    if (!menuName) {
      return;
    }

    const aliasName = String(item?.AliasName || "").trim() || menuName;
    const tokens = [menuName, ...parseReplaceLabels(item?.ReplaceLabels)];

    tokens.forEach((token) => {
      const normalizedToken = normalizeMenuName(token);
      if (!normalizedToken || replacementMap.has(normalizedToken)) {
        return;
      }

      replacementMap.set(normalizedToken, {
        token: String(token || "").trim(),
        alias: aliasName,
      });
    });
  });

  return Array.from(replacementMap.values())
    .filter(({ token, alias }) => token && alias)
    .sort((a, b) => b.token.length - a.token.length);
};

const hasHierarchyMenuAccess = (userData, menuName) => {
  const accessibleMenus = getAccessibleHierarchyMenuSet(userData);
  if (!accessibleMenus.size) {
    return true;
  }

  return accessibleMenus.has(normalizeMenuName(menuName));
};

export const isHierarchyMenuVisible = (userData, menuName) =>
  hasHierarchyMenuAccess(userData, menuName);

const isHierarchyProtectedMenu = (menu) =>
  HIERARCHY_MENU_NAMES.includes(normalizeMenuName(menu?.title));

const filterHierarchyMenuRecursive = (menu, userData) => {
  const filteredSubMenus =
    menu.subMenus
      ?.map((subMenu) => filterHierarchyMenuRecursive(subMenu, userData))
      .filter(Boolean) || [];

  const isAllowed =
    !isHierarchyProtectedMenu(menu) ||
    hasHierarchyMenuAccess(userData, menu.title);

  if (isAllowed || filteredSubMenus.length > 0) {
    return {
      ...menu,
      ...(filteredSubMenus.length > 0 && { subMenus: filteredSubMenus }),
    };
  }

  return null;
};

export const filterSidebarMenusByHierarchyAccess = (menus, userData) => {
  if (!Array.isArray(menus)) {
    return [];
  }

  const accessibleMenus = getAccessibleHierarchyMenuSet(userData);

  if (!accessibleMenus.size) {
    return menus;
  }

  return menus
    .map((menu) => filterHierarchyMenuRecursive(menu, userData))
    .filter(Boolean);
};

const applyHierarchyAliasesRecursive = (menu, userData) => {
  const aliasedTitle =
    typeof menu?.title === "string"
      ? replaceHierarchyTermsWithAlias(menu.title, userData)
      : menu.title;

  return {
    ...menu,
    title: aliasedTitle,
    ...(menu.subMenus?.length
      ? {
          subMenus: menu.subMenus.map((subMenu) =>
            applyHierarchyAliasesRecursive(subMenu, userData),
          ),
        }
      : {}),
  };
};

export const applyHierarchyAliasesToMenus = (menus, userData) => {
  if (!Array.isArray(menus)) {
    return [];
  }

  return menus.map((menu) => applyHierarchyAliasesRecursive(menu, userData));
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const protectFixedHierarchyLabels = (text) => {
  const protectedLabels = new Map();
  let protectedIndex = 0;
  let updatedText = text;

  FIXED_HIERARCHY_LABELS.forEach((label) => {
    const regex = new RegExp(`\\b${escapeRegExp(label)}\\b`, "gi");
    updatedText = updatedText.replace(regex, (match) => {
      const token = `[[HIERARCHY_FIXED_${protectedIndex++}]]`;
      protectedLabels.set(token, match);
      return token;
    });
  });

  return { updatedText, protectedLabels };
};

const restoreFixedHierarchyLabels = (text, protectedLabels) => {
  if (!protectedLabels?.size) {
    return text;
  }

  return text.replace(
    /\[\[HIERARCHY_FIXED_\d+\]\]/g,
    (token) => protectedLabels.get(token) || token,
  );
};

export const replaceHierarchyTermsWithAlias = (text, userData) => {
  //if (typeof text !== "string" || !text.trim()) { //Commented to not replace words based on hierarchy
  return text;
  //}

  const replacementEntries = getHierarchyReplacementEntries(userData);
  if (!replacementEntries.length) {
    return text;
  }

  const { updatedText: protectedText, protectedLabels } =
    protectFixedHierarchyLabels(text);

  const replacedText = replacementEntries.reduce(
    (updatedText, { token, alias }) => {
      const regex = new RegExp(`\\b${escapeRegExp(token)}\\b`, "gi");
      return updatedText.replace(regex, (match) => {
        const isAllCaps = match === match.toUpperCase();
        const isFirstUpper = match[0] === match[0].toUpperCase();

        if (isAllCaps) {
          return alias.toUpperCase();
        }

        if (isFirstUpper) {
          return alias.charAt(0).toUpperCase() + alias.slice(1);
        }

        return alias.toLowerCase();
      });
    },
    protectedText,
  );

  return restoreFixedHierarchyLabels(replacedText, protectedLabels);
};

export const getHierarchyDisplayLabel = (defaultLabel, userData) =>
  replaceHierarchyTermsWithAlias(defaultLabel, userData);

export const filterSidebarMenusByPermission = (menus, allowedMenus) => {
  // Extract allowed codes once for fast lookup
  const allowedCodes = allowedMenus.map((m) => m.MainModuleCode);

  const hasPermission = (menu) => menu.code && allowedCodes.includes(menu.code);

  const filterRecursive = (menu) => {
    let filteredSubMenus = [];

    // 🔁 Recursively process subMenus
    if (menu.subMenus?.length) {
      filteredSubMenus = menu.subMenus.map(filterRecursive).filter(Boolean);
    }

    const isAllowed = hasPermission(menu);

    // ✅ Keep menu if it OR any child is allowed
    if (isAllowed || filteredSubMenus.length > 0) {
      return {
        ...menu,
        ...(filteredSubMenus.length > 0 && { subMenus: filteredSubMenus }),
      };
    }

    return null;
  };

  return menus.map(filterRecursive).filter(Boolean);
};

export function checkAuthorization(allowedMenus) {
  const currentPath = window.location.pathname.toLowerCase();

  if (currentPath === "/" || currentPath === NOACCESS) return true;

  // Extract allowed paths
  const allowedPaths = allowedMenus
    .map((m) => m.MainModulePath?.toLowerCase())
    .filter(Boolean);

  // Check if current URL matches or starts with any allowed path
  const hasAccess = allowedPaths.some(
    (path) => currentPath === path || currentPath.startsWith(path + "/"),
  );

  return hasAccess;
}

export function checkHierarchyAuthorization(userData) {
  const currentPath = window.location.pathname.toLowerCase();

  if (currentPath === "/" || currentPath === NOACCESS) return true;

  const protectedRoute = HIERARCHY_ROUTE_MAP.find(
    ({ route }) =>
      currentPath === route.toLowerCase() ||
      currentPath.startsWith(`${route.toLowerCase()}/`),
  );

  if (!protectedRoute) {
    return true;
  }

  return hasHierarchyMenuAccess(userData, protectedRoute.menuName);
}

const ISO_DATE_TIME_REGEX =
  /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;
const US_DATE_REGEX = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/;
const DOTNET_DATE_REGEX = /\/Date\((\d+)\)\//;

// MM/DD/YYYY and MM/DD/YY are parsed explicitly so the two-digit-year pivot and
// month/day order are deterministic instead of browser dependent.
const parseUsDate = (value) => {
  const match = US_DATE_REGEX.exec(value);
  if (!match) return null;

  const month = Number(match[1]);
  const day = Number(match[2]);
  let year = Number(match[3]);
  if (match[3].length === 2) year += year >= 50 ? 1900 : 2000;

  const parsedDate = new Date(year, month - 1, day);
  if (
    parsedDate.getFullYear() !== year ||
    parsedDate.getMonth() !== month - 1 ||
    parsedDate.getDate() !== day
  ) {
    return null;
  }

  return parsedDate;
};

export const parseDateValue = (dateValue) => {
  if (!dateValue) return null;

  if (dateValue instanceof Date) {
    return Number.isNaN(dateValue.getTime()) ? null : dateValue;
  }

  if (typeof dateValue === "string") {
    const dotNetMatch = dateValue.match(DOTNET_DATE_REGEX);
    if (dotNetMatch) {
      const dotNetDate = new Date(Number(dotNetMatch[1]));
      return Number.isNaN(dotNetDate.getTime()) ? null : dotNetDate;
    }

    const usDate = parseUsDate(dateValue.trim());
    if (usDate) return usDate;
  }

  const parsedDate = new Date(dateValue);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

// Returns a comparable timestamp for recognised date values (Date objects, ISO
// datetimes, MM/DD/YYYY, MM/DD/YY, /Date(ms)/) and null for anything else, so
// callers can fall back to their normal comparison for non-date columns.
export const getDateSortValue = (value) => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.getTime();
  }

  if (typeof value !== "string") return null;

  const trimmedValue = value.trim();
  if (!trimmedValue) return null;

  const isDateLike =
    ISO_DATE_TIME_REGEX.test(trimmedValue) ||
    US_DATE_REGEX.test(trimmedValue) ||
    DOTNET_DATE_REGEX.test(trimmedValue);
  if (!isDateLike) return null;

  const parsedDate = parseDateValue(trimmedValue);
  return parsedDate ? parsedDate.getTime() : null;
};

// Compares two cell values chronologically. Returns null when either value is
// not a recognisable date so the caller keeps its existing comparison.
export const compareDateValues = (aValue, bValue) => {
  const aTime = getDateSortValue(aValue);
  if (aTime === null) return null;

  const bTime = getDateSortValue(bValue);
  if (bTime === null) return null;

  return aTime - bTime;
};

export const getDateSortValueWithEmptyLast = (dateValue) => {
  const parsedDate = parseDateValue(dateValue);

  if (!parsedDate) return Number.MAX_SAFE_INTEGER;

  return parsedDate.getTime();
};

export function formatSafeDate(dateValue) {
  const parsedDate = parseDateValue(dateValue);
  if (!parsedDate) return "-";

  return parsedDate.toLocaleDateString("en-US", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  });
}

export function formatSafeDateWithFullYear(dateValue) {
  const parsedDate = parseDateValue(dateValue);
  if (!parsedDate) return "-";

  return parsedDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

export function formatDecimalAmount(val) {
  const num = parseFloat(val);
  if (isNaN(num)) return "-";
  return num.toFixed(2);
}

export function formatCapacityNumber(value) {
  const num = Number(value) || 0;
  return Number.isInteger(num) ? num : num.toFixed(2);
}

// Resolves a selected Primary Work Location down to its id, whether the
// caller passed the full dropdown option object ({ value, label } from
// transformDropdownOptions), a raw numeric id, or a string id. Centralizing
// this means callers never compare option objects directly against a
// Capacity API row.
export function getPrimaryWorkLocationId(primaryWorkLocation) {
  if (
    primaryWorkLocation === null ||
    primaryWorkLocation === undefined ||
    primaryWorkLocation === ""
  ) {
    return null;
  }

  if (typeof primaryWorkLocation === "object") {
    return primaryWorkLocation.value ?? null;
  }

  return primaryWorkLocation;
}

// Shared by the Capacity table (editRadProfileDuplicate) and the Capacity
// Breakdown modal (RadCapacityDetailsModal) so both highlight the same row
// for a given Primary Work Location selection. `row` fields are the actual
// Capacity API response fields (StaffPrimaryLocationId / StaffPrimaryLocationName).
export function isPrimaryWorkLocationRow(row, primaryWorkLocation) {
  if (!row) return false;

  const selectedId = getPrimaryWorkLocationId(primaryWorkLocation);
  if (selectedId != null && row.StaffPrimaryLocationId != null) {
    return String(row.StaffPrimaryLocationId) === String(selectedId);
  }

  // Fallback when an id isn't resolvable on one side - compare the name,
  // trimmed and case-insensitive.
  const selectedName =
    typeof primaryWorkLocation === "object"
      ? primaryWorkLocation?.label
      : primaryWorkLocation;
  const rowName = String(row.StaffPrimaryLocationName || "")
    .trim()
    .toLowerCase();
  const normalizedSelectedName = String(selectedName || "")
    .trim()
    .toLowerCase();

  return (
    !!rowName && !!normalizedSelectedName && rowName === normalizedSelectedName
  );
}

export const sortItemsByName = (items, order = "asc", nameKey = "Name") => {
  const normalizedItems = Array.isArray(items) ? [...items] : [];
  const direction = order === "desc" ? -1 : 1;

  return normalizedItems.sort((firstItem, secondItem) => {
    const firstName = String(firstItem?.[nameKey] || "").trim();
    const secondName = String(secondItem?.[nameKey] || "").trim();

    return (
      firstName.localeCompare(secondName, undefined, {
        sensitivity: "base",
        numeric: true,
      }) * direction
    );
  });
};

const WEEKDAY_ORDER = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];
const WEEKDAY_ABBREVIATION_ORDER = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
];

const getWeekdayRank = (value) => {
  const normalized = value.toString().trim().toLowerCase();
  const fullNameRank = WEEKDAY_ORDER.indexOf(normalized);
  return fullNameRank !== -1
    ? fullNameRank
    : WEEKDAY_ABBREVIATION_ORDER.indexOf(normalized);
};

// Generic column-filter option sorter used by the shared table components
// (CollapsibleCustomizeTable, AdvancedTable, Table). Most filter columns are
// free-form text, so the default is a plain alphabetical sort. When every
// unique value in the column is a weekday name (or its 3-letter
// abbreviation), it sorts Monday -> Sunday instead - this only activates for
// that specific case, so every other column/filter type is unaffected.
export const sortFilterOptionValues = (values) => {
  const normalizedValues = Array.isArray(values) ? values : [];
  const isAllWeekdays =
    normalizedValues.length > 0 &&
    normalizedValues.every((value) => getWeekdayRank(value) !== -1);

  if (isAllWeekdays) {
    return [...normalizedValues].sort(
      (a, b) => getWeekdayRank(a) - getWeekdayRank(b),
    );
  }

  return [...normalizedValues].sort((a, b) =>
    a.toString().localeCompare(b.toString()),
  );
};

export const normalizeColumnWidths = (columns) => {
  if (!Array.isArray(columns) || columns.length === 0) {
    return columns;
  }

  const columnsWithWidth = columns.filter(
    (column) =>
      typeof column.width === "string" && column.width.trim().endsWith("%"),
  );

  // If there are no percentage widths, don't modify anything
  if (columnsWithWidth.length === 0) {
    return columns;
  }

  const totalWidth = columnsWithWidth.reduce(
    (sum, column) => sum + parseFloat(column.width),
    0,
  );

  // IMPORTANT:
  // If total width is <= 100%, keep the original widths.
  // Do NOT force them to 100%, because the vertical scrollbar
  // reduces the actual available table width.
  if (totalWidth <= 100) {
    return columns;
  }

  // Keep a small buffer below 100% to prevent horizontal overflow.
  const targetWidth = 98;

  const ratio = targetWidth / totalWidth;

  return columns.map((column) => {
    if (
      typeof column.width !== "string" ||
      !column.width.trim().endsWith("%")
    ) {
      return column;
    }

    const width = parseFloat(column.width);

    return {
      ...column,
      width: `${(width * ratio).toFixed(2)}%`,
    };
  });
};
